import * as React from 'react';

import type { ColorSchemePreference, ColorSchemeRenderedOnTheClient } from 'bundles/appearance/types';

import {
  clearColorSchemePreference,
  clearDarkModeBannerDismissedInLocalStorage,
  getColorSchemeRenderedOnTheClient,
  getInitialColorTheme,
  getThemeFromUrl,
  isDarkThemeEnabled,
  setColorSchemePreference,
} from '../utils';

const ColorThemeContext = React.createContext<{
  colorTheme: ColorSchemePreference;
  onColorThemeChange: (colorTheme: ColorSchemePreference) => void;
  colorThemeRenderedOnTheClient: ColorSchemeRenderedOnTheClient;
}>({
  colorTheme: 'light',
  onColorThemeChange: () => {},
  colorThemeRenderedOnTheClient: 'light',
});

export const useColorTheme = () => {
  return React.useContext(ColorThemeContext);
};

const SyncHtmlAtribute = () => {
  const { colorTheme, colorThemeRenderedOnTheClient } = useColorTheme();
  React.useEffect(() => {
    document.documentElement.setAttribute('data-color-theme', colorTheme);
  }, [colorTheme]);
  React.useEffect(() => {
    // used for Pendo to detect dark mode
    document.documentElement.setAttribute('data-rendered-theme', colorThemeRenderedOnTheClient);
  }, [colorThemeRenderedOnTheClient]);
  return null;
};

export const ColorThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [colorTheme, setColorTheme] = React.useState<ColorSchemePreference>(getInitialColorTheme);
  const [colorThemeRenderedOnTheClient, setColorThemeRenderedOnTheClient] =
    React.useState<ColorSchemeRenderedOnTheClient>(getColorSchemeRenderedOnTheClient(colorTheme));

  React.useEffect(() => {
    const themeFromUrl = getThemeFromUrl();
    if (themeFromUrl) {
      setColorSchemePreference(themeFromUrl as ColorSchemePreference);
    }
  }, []);

  React.useEffect(() => {
    const renderedOnTheClient = getColorSchemeRenderedOnTheClient(colorTheme);
    setColorThemeRenderedOnTheClient(renderedOnTheClient);
    if (colorTheme === 'system') {
      const handleMediaQueryChange = () => {
        setColorThemeRenderedOnTheClient(getColorSchemeRenderedOnTheClient(colorTheme));
      };
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', handleMediaQueryChange);
      return () => {
        window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', handleMediaQueryChange);
      };
    } else {
      return () => {};
    }
  }, [colorTheme]);

  // Kill switch: clear the cookie and reset to light theme if dark mode is disabled
  React.useEffect(() => {
    if (!isDarkThemeEnabled()) {
      clearColorSchemePreference();
      clearDarkModeBannerDismissedInLocalStorage();
      setColorTheme('light');
    }
  }, []);

  return (
    <ColorThemeContext.Provider
      value={{ colorTheme, onColorThemeChange: setColorTheme, colorThemeRenderedOnTheClient }}
    >
      <SyncHtmlAtribute />
      {children}
    </ColorThemeContext.Provider>
  );
};
